import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoginResponse, UserResponse } from "../../api/authApi";

export const SESSION_REPLACED_STORAGE_KEY = "adut.sessionReplacedMessage";
export const SESSION_REPLACED_MESSAGE =
  "You were signed out because this account was signed in on another browser.";

let sessionReplacedMessageCache: string | null = null;
let sessionReplacedMessageRead = false;

export const consumeSessionReplacedMessage = (): string | null => {
  if (!sessionReplacedMessageRead) {
    sessionReplacedMessageRead = true;
    sessionReplacedMessageCache =
      sessionStorage.getItem(SESSION_REPLACED_STORAGE_KEY);
    if (sessionReplacedMessageCache) {
      sessionStorage.removeItem(SESSION_REPLACED_STORAGE_KEY);
    }
  }

  return sessionReplacedMessageCache;
};

export const resetSessionReplacedMessage = (): void => {
  sessionReplacedMessageRead = false;
  sessionReplacedMessageCache = null;
  sessionStorage.removeItem(SESSION_REPLACED_STORAGE_KEY);
};

interface AuthState {
  user: UserResponse | null;
  token: string | null;
  refreshToken: string | null;
  tokenExpiration: number | null;
  isAuthenticated: boolean;
  isLoggingOff: boolean;
}

const loadState = (): AuthState => {
  try {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    const tokenExpiration = localStorage.getItem("tokenExpiration");
    const userString = localStorage.getItem("user");

    if (token && refreshToken && tokenExpiration && userString && userString !== 'undefined') {
      const user = JSON.parse(userString) as UserResponse;
      return {
        user,
        token,
        refreshToken,
        tokenExpiration: parseInt(tokenExpiration),
        isAuthenticated: true,
        isLoggingOff: false,
      };
    }
  } catch (e) {}

  return {
    user: null,
    token: null,
    refreshToken: null,
    tokenExpiration: null,
    isAuthenticated: false,
    isLoggingOff: false,
  };
};

const initialState: AuthState = loadState();

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      const { user, accessToken, refreshToken } = action.payload;
      // JWT expires in 15 minutes by default from backend
      const tokenExpiration = Date.now() + 15 * 60 * 1000; // 15 minutes in milliseconds

      state.user = user;
      state.token = accessToken;
      state.refreshToken = refreshToken;
      state.tokenExpiration = tokenExpiration;
      state.isAuthenticated = true;
      state.isLoggingOff = false;

      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("tokenExpiration", tokenExpiration.toString());
      localStorage.setItem("user", JSON.stringify(user));
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.tokenExpiration = null;
      state.isAuthenticated = false;
      state.isLoggingOff = false;

      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenExpiration");
      localStorage.removeItem("user");
    },
    startLoggingOff: (state) => {
      state.isLoggingOff = true;
    },
  },
});

export const { setCredentials, logout, startLoggingOff } = authSlice.actions;

export const selectCurrentUser = (state: { auth: AuthState }) =>
  state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;
export const selectRefreshToken = (state: { auth: AuthState }) =>
  state.auth.refreshToken;
export const selectTokenExpiration = (state: { auth: AuthState }) =>
  state.auth.tokenExpiration;
export const selectIsLoggingOff = (state: { auth: AuthState }) =>
  state.auth.isLoggingOff;

export default authSlice.reducer;
